import { Link } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

/**
 * ProductCard
 * - default: soft-UI card (subtle shadow, hover lift)
 * - variant="glass": glassmorphism styling for hero/trending strips
 */
export default function ProductCard({ product, variant = "default" }) {
  const { add } = useCart();

  const wrapperCls =
    variant === "glass"
      ? "group relative rounded-3xl p-4 border border-white/60 shadow-[0_10px_40px_-16px_rgba(10,25,47,0.25)] hover:-translate-y-1.5 transition-transform duration-300"
      : "group";
  const wrapperStyle =
    variant === "glass"
      ? { background: "rgba(255,255,255,0.55)", backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)" }
      : undefined;

  return (
    <div
      className={wrapperCls}
      style={wrapperStyle}
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/product/${product.slug}`} className="block">
        <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-[#F4EBE1] flex items-center justify-center">
          <img
            src={abs(product.image_url)}
            alt={product.name}
            className="product-img h-full w-full object-contain p-4"
            loading="lazy"
          />
          {product.compare_at_price && product.compare_at_price > product.price && (
            <span className="absolute top-3 left-3 bg-[#E65A28] text-white text-xs font-semibold px-3 py-1 rounded-full shadow-md">
              {Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)}% OFF
            </span>
          )}
          {variant === "glass" && (
            <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest bg-[#0A192F] text-white px-2 py-1 rounded-full">Trending</span>
          )}
        </div>
      </Link>
      <div className="mt-4 flex items-start justify-between gap-3">
        <div className="flex-1">
          <Link to={`/product/${product.slug}`}>
            <h3 className="font-serif text-lg text-[#0A192F] leading-snug hover:text-[#E65A28] transition-colors">
              {product.name}
            </h3>
          </Link>
          <p className="text-xs uppercase tracking-widest text-[#666] mt-1">{product.category_slug.replace(/-/g, " ")}</p>
          <div className="flex items-baseline gap-2 mt-2">
            <span className="text-lg font-semibold text-[#0A192F]">₹{product.price}</span>
            {product.compare_at_price && (
              <span className="text-sm text-[#666] line-through">₹{product.compare_at_price}</span>
            )}
          </div>
        </div>
        <button
          onClick={(e) => { e.preventDefault(); add(product); }}
          className="flex-shrink-0 h-10 w-10 rounded-full bg-[#0A192F] text-white flex items-center justify-center hover:bg-[#E65A28] hover:scale-110 transition-all duration-200 shadow-md"
          data-testid={`add-to-cart-${product.slug}`}
          aria-label="Add to cart"
        >
          <ShoppingBag className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
